import type { ReactNode } from 'react'
import type { Member } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

interface AuthContextValue {
    member: Member | null
    ready: boolean
    refresh: () => Promise<void>
    setMember: (member: Member | null) => void
    signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
    member: null,
    ready: false,
    refresh: async () => {},
    setMember: () => {},
    signOut: async () => {}
})

interface AuthProviderProps {
    children: ReactNode
}

/** Knows whether a member is signed in, so the navigation can say so. */
export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [member, setMember] = useState<Member | null>(null)
    const [ready, setReady] = useState(false)

    const refresh = useCallback(async () => {
        try {
            const response = await fetch('/api/auth/me', { cache: 'no-store' })
            const data = await response.json().catch(() => null)

            setMember(response.ok && data?.member ? data.member : null)
        }
        catch {
            setMember(null)
        }
        finally {
            setReady(true)
        }
    }, [])

    const signOut = useCallback(async () => {
        try {
            await fetch('/api/auth/logout', { method: 'POST' })
        }
        finally {
            setMember(null)
        }
    }, [])

    useEffect(() => {
        refresh()
    }, [refresh])

    const value = useMemo(() => ({ member, ready, refresh, setMember, signOut }), [member, ready, refresh, signOut])

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = (): AuthContextValue => useContext(AuthContext)
